import PropTypes from 'prop-types';
import React from 'react';

import CoAuthorsList from '@/src/coAuthors/components/CoAuthorsList';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import ContentCreatorWidget from '../../contentCreatorWidget/containers/ContentCreatorWidget';
import ReviewsList from '../../reviewsWidget/containers/ReviewsList';
import {SectionContainer, Text} from '../../ui';
import css from '../styles/CourseDetailsLayout.module.css';
import CourseInfo from './CourseInfo';
import CoursePreview from './coursePreview/CoursePreview';
import CourseTopicsDetails from './courseTopicsDetails/CourseTopicsDetails';
import CourseTopInfo from './courseTopInfo/CourseTopInfo';

const CourseDetailsLayout = ({
  topicsCount,
  isBookmarked,
  isBookmarkedLoading,
  onCourseBookMarkToggleClick,
  courseTitle,
  courseDescription,
  courseRequirements,
  courseTargetGroups,
  courseCategories,
  rating,
  updateDate,
  difficultyLevel,
  contentCreator,
  topics,
  coursePreviewVideo,
  coursePreviewImage,
  courseId,
  duration,
  hasCertificate,
  languages,
  availableTranslations,
  onWatchCourseClick,
  isWritingReviewAllowed,
  toggleLeaveReviewAllowed,
  ratingCount,
  course,
}) => {
  return (
    <div>
      <CourseTopInfo
        courseTitle={courseTitle}
        updateDate={updateDate}
        courseCreator={contentCreator}
        difficultyLevel={difficultyLevel}
        topicsCount={topicsCount}
        rating={rating}
        courseCategories={courseCategories}
        courseDescription={courseDescription}
        ratingCount={ratingCount}
        surgeryDate={course.surgeryDate}
        coAuthors={course.coAuthors}
        isPublic={course.isPublic}
      />
      <SectionContainer>
        <div className={css.courseDetailsMainInfo}>
          <div className={css.topicsListWrapper}>
            <div>
              <div className={css.topicsListTitle}>
                <Text
                  fontSize={Text.FONT_SIZE.SIZE_32}
                  fontFamily={Text.FONT_FAMILY.RUBIK}
                  lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                  tagComponent={Text.TAG.SPAN}
                  fontWeight={Text.FONT_WEIGHT.MEDIUM}
                >
                  <TextLocal id={'courseDetails.course-content'} />
                </Text>
              </div>
              <div className={css.topicsListInfo}>
                <Text
                  fontSize={Text.FONT_SIZE.SIZE_14}
                  lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1}
                  color={Text.COLOR.GREY}
                >
                  {topicsCount}{' '}
                  <TextLocal
                    id={
                      topicsCount > 1
                        ? 'courseDetails.topics'
                        : 'courseDetails.topic'
                    }
                  />{' '}
                  | <TextLocal id={difficultyLevel.label} />
                </Text>
              </div>
              <CourseTopicsDetails
                topics={topics}
                contentCreator={contentCreator.name}
              />
            </div>

            {Boolean(courseDescription?.length) && (
              <CourseInfo
                title={'courseDetails.description'}
                text={courseDescription}
              />
            )}
            {Boolean(courseRequirements?.length) && (
              <CourseInfo
                title={'courseDetails.requirements'}
                text={courseRequirements}
              />
            )}
            {Boolean(courseTargetGroups?.length) && (
              <CourseInfo
                title={'courseDetails.target-groups'}
                text={courseTargetGroups}
              />
            )}

            <div className={css.contentCreatorWrapper}>
              <div className={css.contentCreatorWrapperTitle}>
                <Text
                  fontSize={Text.FONT_SIZE.SIZE_36}
                  fontFamily={Text.FONT_FAMILY.RUBIK}
                  lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                  tagComponent={Text.TAG.SPAN}
                  fontWeight={Text.FONT_WEIGHT.MEDIUM}
                >
                  <TextLocal id={'courseDetails.content-creator'} />
                </Text>
              </div>
              <ContentCreatorWidget
                key={course.slug}
                userId={contentCreator.id}
                isShowUserCourses
                excludedCourses={[course.courseId]}
              />
            </div>

            <CoAuthorsList list={course.coAuthors} />

            <div className={css.reviewsWrapper}>
              <div>
                <ReviewsList
                  key={course.slug}
                  courseId={courseId}
                  toggleLeaveReviewAllowed={toggleLeaveReviewAllowed}
                  isWritingReviewAllowed={isWritingReviewAllowed}
                />
              </div>
            </div>
          </div>

          <div className={css.courseInfoParent}>
            <div className={css.courseInfoChild}>
              <div>
                <CoursePreview
                  onWatchCourseClick={onWatchCourseClick}
                  isBookmarkedLoading={isBookmarkedLoading}
                  onCourseBookMarkToggleClick={onCourseBookMarkToggleClick}
                  isBookmarked={isBookmarked}
                  video={coursePreviewVideo}
                  preview={coursePreviewImage}
                  // @ts-ignore
                  courseId={courseId}
                  title={courseTitle}
                  duration={duration}
                  hasCertificate={hasCertificate}
                  topicsCount={topicsCount}
                  difficultyLevel={difficultyLevel}
                  languages={languages}
                  availableTranslations={availableTranslations}
                />
              </div>
            </div>
          </div>
        </div>
      </SectionContainer>
    </div>
  );
};

CourseDetailsLayout.propTypes = {
  topicsCount: PropTypes.number.isRequired,
  courseTitle: PropTypes.string.isRequired,
  courseDescription: PropTypes.string.isRequired,
  courseRequirements: PropTypes.string.isRequired,
  courseTargetGroups: PropTypes.string.isRequired,
  courseCategories: PropTypes.array.isRequired,
  rating: PropTypes.number.isRequired,
  updateDate: PropTypes.string.isRequired,
  difficultyLevel: PropTypes.object.isRequired,
  contentCreator: PropTypes.object.isRequired,
  topics: PropTypes.array.isRequired,
  coursePreviewVideo: PropTypes.string.isRequired,
  coursePreviewImage: PropTypes.string.isRequired,
  courseId: PropTypes.number.isRequired,
  duration: PropTypes.string.isRequired,
  hasCertificate: PropTypes.string.isRequired,
  languages: PropTypes.array.isRequired,
  availableTranslations: PropTypes.array.isRequired,
  isBookmarked: PropTypes.bool.isRequired,
  isBookmarkedLoading: PropTypes.bool.isRequired,
  isWritingReviewAllowed: PropTypes.bool.isRequired,
  onCourseBookMarkToggleClick: PropTypes.func.isRequired,
  onWatchCourseClick: PropTypes.func.isRequired,
  toggleLeaveReviewAllowed: PropTypes.func.isRequired,
  ratingCount: PropTypes.number.isRequired,
  reviews: PropTypes.array.isRequired,
  course: PropTypes.object.isRequired,
};

CourseDetailsLayout.defaultProps = {
  courseCategories: [],
  languages: [],
  availableTranslations: [],
  difficultyLevel: {},
};

export default CourseDetailsLayout;
